import { useMemo } from 'react';
import { GridApi } from '@ag-grid-community/core';
import { logger } from '@urc-ui/services/logger';
import { AdvertiserInfo } from '@urc-ui/store/api/advertisers/types';
import { PREFETCH_ADVERTISER_COUNT } from '@urc-ui/store/constants';
import { buildGetAdvertiserInfoRequest, useGetAdvertiserInfoQuery } from '@urc-ui/store/api/advertisers/advertisers';
import { buildGetSubscriptionsRequest, useGetSubscriptionsQuery } from '@urc-ui/store/api/subscriptions/subscriptions';
import { getEntityId } from '@urc-ui/store/api/base';
import { TFunction, useTranslation } from 'react-i18next';
import { Subscription } from '@urc-ui/store/api/subscriptions/types';

interface UseAdvertiserInfoResult {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  advertiserInfoById: { [index: string]: AdvertiserInfo };
}

export const useGetAdvertiserInfoById = (advertiserIds: string[]) => {
  const prefetching = useGetAdvertiserInfoQuery(buildGetAdvertiserInfoRequest());
  const skipFetching =
    prefetching.isError || prefetching.isLoading || (prefetching.isSuccess && prefetching.data.totalResults <= PREFETCH_ADVERTISER_COUNT);
  const { data, isSuccess, isFetching, isError, error } = useGetAdvertiserInfoQuery(buildGetAdvertiserInfoRequest(advertiserIds), {
    skip: skipFetching
  });

  return useMemo(() => {
    const result: UseAdvertiserInfoResult = {
      isLoading: false,
      isSuccess: false,
      isError: false,
      advertiserInfoById: {}
    };

    if (prefetching.isError) {
      logger.logFatalMessage('Error occurred while prefetching advertiser info', prefetching.error);
      result.isError = true;
      return result;
    }

    if (isError) {
      logger.logFatalMessage('Error occurred while fetching advertiser info', error);
      result.isError = true;
      return result;
    }

    if (prefetching.isLoading || isFetching) {
      result.isLoading = true;
      return result;
    }

    result.isSuccess = prefetching.isSuccess && (skipFetching || isSuccess);
    if (prefetching.isSuccess && prefetching.data.totalResults <= PREFETCH_ADVERTISER_COUNT) {
      result.advertiserInfoById = prefetching.data.advertiserInfoById;
    } else if (isSuccess) {
      result.advertiserInfoById = data.advertiserInfoById;
    }

    return result;
  }, [prefetching, isFetching, isSuccess]);
};

export const getAdvertiserIdsFromSubscriptions = (subscriptions: Subscription[]) => {
  const advertiserIds = subscriptions.flatMap(subscription => subscription.accounts).filter(account => account !== 'ALL_ADVERTISERS');
  return Array.from(new Set(advertiserIds));
};

const mapAdvertiserInfo = (ids: string[], mapping: Map<string, AdvertiserInfo>, translate: TFunction) => {
  const missingIds = [] as string[];
  const advertiserInfo = ids.map(id => {
    if (mapping.has(id)) {
      return {
        name: mapping.get(id)?.name,
        cfid: mapping.get(id)?.cfid
      };
    } else if (id === 'ALL_ADVERTISERS') {
      return { name: translate('sspa_rcm_ad_reports_column_header_all_advertisers', 'All Advertisers') };
    }
    missingIds.push(id);
    return { name: translate('sspa_rcm_ad_reports_unidentified_advertiser', 'Unidentified Advertiser') };
  }) as AdvertiserInfo[];

  if (missingIds.length > 0) {
    logger.logErrorMessage(`There were unidentified advertisers [${missingIds.join(', ')}] in entityId: ${getEntityId()}`);
  }
  return advertiserInfo;
};

export const useAdvertisers = (accounts: string[], api: GridApi) => {
  const paginationSize = api.paginationGetPageSize();
  const currentPage = api.paginationGetCurrentPage();
  const filterModel = api.getFilterModel();
  const request = useMemo(
    () => buildGetSubscriptionsRequest(paginationSize, currentPage, filterModel),
    [paginationSize, currentPage, filterModel]
  );
  const { data, isSuccess, isLoading } = useGetSubscriptionsQuery(request);
  const advertiserIds = getAdvertiserIdsFromSubscriptions(isSuccess ? data.subscriptions : []) as string[];
  const advertiserInfo = useGetAdvertiserInfoById(advertiserIds);
  const { t: translate } = useTranslation();

  return {
    isLoading: isLoading || advertiserInfo.isLoading,
    advertisers:
      advertiserInfo.isSuccess && isSuccess
        ? mapAdvertiserInfo(accounts, new Map(Object.entries(advertiserInfo.advertiserInfoById)), translate)
        : []
  };
};
